import { globalLogger } from "@common/core/Logger";
import { FeatureStatusPublisher } from "@common/core/FeatureStatusPublisher";

/**
 * Public mortgages root (`restless-mortgages` `public` bundle).
 *
 * Composition root for the five public mortgage calculators. The legacy
 * `public.js` concatenated (`//=include`) the shared form helpers, the
 * Handlebars helpers and the five calculators into one eager file; each is now
 * its own module in the layered tree.
 *
 * Every calculator is a **page-specific** feature — present only on the pages
 * that host it — so each is activated through a DOM-guarded dynamic `import()`
 * (per `.claude/rules/frontend/modules-and-code-splitting.md`). A page that
 * doesn't host a given calculator never fetches or parses its chunk. This is
 * what lets esbuild split jQuery and Handlebars — used only by the
 * equity-release calculator and lead-gen — out of the entry, keeping them off
 * the vanilla-only pages (quote, buy-to-let, mortgage-payment). The Handlebars
 * helper registration rides with the equity-release calculator, the only
 * feature that renders a Handlebars template.
 *
 * jQuery and Handlebars are bundled from npm (the bundled jQuery is
 * encapsulated and does not touch the page's global jQuery). `restlessConfig`,
 * `window.RestLess`, `window.er_quote` and `window.initInfobubbles` remain
 * external globals, as before. Each feature is DOM-guarded, and its activation
 * is isolated so a failure in one never prevents the others from mounting.
 */

type Feature = {
  name: string;
  selector: string;
  activate: () => Promise<void>;
};

const features: Feature[] = [
  {
    name: "EquityReleaseCalculator",
    selector: "#equity-release",
    activate: async () => {
      const { registerMortgageHelpers } = await import("@common/components/mortgages/handlebarsHelpers");
      const { initEquityReleaseCalculator } = await import("@common/services/mortgages/equityReleaseCalculator");
      registerMortgageHelpers();
      initEquityReleaseCalculator();
    }
  },
  {
    name: "EquityReleaseQuote",
    selector: "#equity-release-quote",
    activate: async () => {
      const { initEquityReleaseQuote } = await import("@common/services/mortgages/equityReleaseQuote");
      initEquityReleaseQuote();
    }
  },
  {
    name: "EquityReleaseLeadGen",
    selector: "#equity-release-lead-gen",
    activate: async () => {
      const { initEquityReleaseLeadGen } = await import("@common/services/mortgages/equityReleaseLeadGen");
      initEquityReleaseLeadGen();
    }
  },
  {
    name: "BuyToLetCalculator",
    selector: ".buy-to-let-calculator",
    activate: async () => {
      const { initBuyToLetCalculator } = await import("@common/services/mortgages/buyToLetCalculator");
      initBuyToLetCalculator();
    }
  },
  {
    name: "MortgagePaymentCalculator",
    selector: ".mortgage-payment-calculator",
    activate: async () => {
      const { initMortgagePaymentCalculator } = await import("@common/services/mortgages/mortgagePaymentCalculator");
      initMortgagePaymentCalculator();
    }
  }
];

const featureStatus = new FeatureStatusPublisher(globalLogger);

for (const feature of features) {
  const roots = document.querySelectorAll<HTMLElement>(feature.selector);
  if (roots.length === 0) {
    continue;
  }

  feature
    .activate()
    .then(() => featureStatus.markReady(feature.name, roots))
    .catch((error) => {
      featureStatus.markFailed(feature.name, roots);
      globalLogger.error(`Failed to initialise mortgage feature [${feature.name}]`, error);
    });
}
